import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../lib/config';
import { AuthCard, Field, inputClass, primaryButtonClass, ErrorBanner, NoticeBanner } from '../components/AuthCard';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const invalid = params.get('error') === 'INVALID_TOKEN' || !token;
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const mismatch = useMemo(
    () => confirmPassword.length > 0 && password !== confirmPassword,
    [password, confirmPassword],
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) {
      setError('Passwords do not match.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      if (!res.ok) {
        setError(body?.message || 'Could not reset password.');
        return;
      }
      navigate('/login?reset=1', { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Choose a new password"
      subtitle="Set a new password so you can get back to your communication records."
      footer={<Link to="/login" className="font-semibold text-amber-700 hover:text-amber-800 hover:underline">Back to sign in</Link>}
    >
      <ErrorBanner message={error} />
      {invalid ? (
        <NoticeBanner tone="amber">
          This reset link is invalid or expired. Request a new one from the reset page.
        </NoticeBanner>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="New password">
            <input
              type="password"
              required
              minLength={10}
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={e => setPassword(e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Confirm password" error={mismatch ? 'Passwords do not match.' : undefined}>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className={inputClass}
            />
          </Field>
          <button type="submit" disabled={submitting || mismatch} className={primaryButtonClass}>
            {submitting ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
      )}
    </AuthCard>
  );
}
